/**
 * Price Guard — client-side price verification for product cards and look cards.
 *
 * Shopify's CDN caches collection pages aggressively with no public purge API.
 * When product prices change (e.g., inventory sync removes DIS discounts), the
 * cached page still shows stale prices. This module verifies displayed prices
 * against live product JSON and patches mismatches in the DOM.
 *
 * Covers:
 *   - Product cards on collection pages  (.s-product-card via [data-product-card])
 *   - Look cards on PDP pages            (.s-pdp__look-card via [data-look-product-id])
 *
 * Performance:
 *   - IntersectionObserver — only verifies cards as they scroll into view
 *   - Max 4 concurrent fetches
 *   - Per-handle cache — same product fetched once
 *   - 1s delay after DOMContentLoaded — doesn't block initial render
 *
 * GitHub: #147
 */
(function () {
  'use strict';

  var MAX_CONCURRENT = 4;
  var productCache = {};
  var queue = [];
  var activeCount = 0;

  /* ── Helpers ────────────────────────────────────────────────────────── */

  function extractHandle(url) {
    var m = (url || '').match(/\/products\/([^?#]+)/);
    return m ? m[1] : null;
  }

  function extractVariantId(url) {
    var m = (url || '').match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  function fmt(dollars) {
    return '$' + parseFloat(dollars).toFixed(2);
  }

  function parsePrices(el) {
    return (el.textContent.match(/\$[\d.]+/g) || []);
  }

  /* ── Fetch queue ────────────────────────────────────────────────────── */

  function enqueue(handle, callback) {
    if (productCache[handle]) {
      callback(productCache[handle]);
      return;
    }
    queue.push({ handle: handle, callback: callback });
    drain();
  }

  function drain() {
    while (activeCount < MAX_CONCURRENT && queue.length > 0) {
      (function (item) {
        activeCount++;
        // Check cache again — may have been populated while queued
        if (productCache[item.handle]) {
          activeCount--;
          item.callback(productCache[item.handle]);
          drain();
          return;
        }
        fetch('/products/' + item.handle + '.json')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            productCache[item.handle] = data.product;
            item.callback(data.product);
          })
          .catch(function () {
            item.callback(null);
          })
          .finally(function () {
            activeCount--;
            drain();
          });
      })(queue.shift());
    }
  }

  /* ── Variant finders ────────────────────────────────────────────────── */

  /** Find variant by ID in product data */
  function findVariantById(product, id) {
    if (!id || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === id) return product.variants[i];
    }
    return null;
  }

  /** Mirror Liquid logic: prefer first available discounted variant, then first available.
   *  Note: .json endpoint omits `available` — treat undefined as available. */
  function selectCardVariant(product) {
    if (!product.variants) return null;
    var firstAvail = null;
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (v.available === false) continue;
      if (!firstAvail) firstAvail = v;
      if (v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price)) {
        return v; // Prefer discounted variant (matches product-card.liquid #169)
      }
    }
    return firstAvail || product.variants[0] || null;
  }

  /* ── Patch detection ────────────────────────────────────────────────── */

  function needsPatch(displayedPrices, livePrice, liveCompare) {
    var livePriceStr = fmt(livePrice);
    if (liveCompare > livePrice) {
      // Sale variant: expect 2 prices (compare + sale)
      var liveCompareStr = fmt(liveCompare);
      return displayedPrices.length !== 2 ||
             displayedPrices[0] !== liveCompareStr ||
             displayedPrices[1] !== livePriceStr;
    }
    // Non-sale: expect exactly 1 price
    return displayedPrices.length !== 1 || displayedPrices[0] !== livePriceStr;
  }

  /* ── Product card verification ──────────────────────────────────────── */

  function verifyProductCard(card) {
    var link = card.querySelector('[data-card-link]');
    if (!link) return;
    var handle = extractHandle(link.getAttribute('href'));
    if (!handle) return;

    enqueue(handle, function (product) {
      if (!product) return;

      // Find the variant the card links to
      var variantId = extractVariantId(link.getAttribute('href'));
      var variant = findVariantById(product, variantId);

      // If linked variant is gone/unavailable, re-select first available.
      // Note: .json endpoint omits `available` (undefined) — only re-select
      // when explicitly false, not when missing.
      if (!variant || variant.available === false) {
        variant = selectCardVariant(product);
      }
      if (!variant) return;

      var priceEl = card.querySelector('.s-product-card__price');
      if (!priceEl) return;

      var livePrice = parseFloat(variant.price);
      var liveCompare = variant.compare_at_price ? parseFloat(variant.compare_at_price) : 0;
      var displayed = parsePrices(priceEl);

      if (!needsPatch(displayed, livePrice, liveCompare)) return;

      // ── Patch price ──
      var badgeEl = card.querySelector('.s-product-card__badge');
      if (liveCompare > livePrice) {
        // Sale variant: show strikethrough + sale price + badge
        priceEl.innerHTML = '<s class="s-product-card__compare-price">' + fmt(liveCompare) + '</s>\n      ' + fmt(livePrice);
        // product-card.liquid only emits the badge span when an available
        // variant had compare_at > price at SSR time. On stale CDN cache
        // (variant went on sale after render) the node is absent, so we
        // must inject it — otherwise the price gets a strikethrough but
        // no badge ever appears.
        if (!badgeEl) {
          var wrap = card.querySelector('.s-product-card__image-wrap');
          if (wrap) {
            badgeEl = document.createElement('span');
            badgeEl.className = 's-product-card__badge';
            wrap.appendChild(badgeEl);
          }
        }
        if (badgeEl) {
          // Floor to match Liquid `divided_by` in product-card.liquid:175.
          // Invariant: displayed % must never overstate the actual discount.
          var pct = Math.floor((liveCompare - livePrice) / liveCompare * 100);
          badgeEl.textContent = pct + '% OFF';
          badgeEl.style.display = '';
        }
      } else {
        // Non-sale: plain price, hide badge
        priceEl.textContent = fmt(livePrice);
        if (badgeEl) badgeEl.style.display = 'none';
      }

      // ── Patch link ──
      var baseUrl = link.getAttribute('href').split('?')[0];
      link.setAttribute('href', baseUrl + '?variant=' + variant.id);

      // ── Patch filter data attributes ──
      var pMin = Infinity, pMax = -Infinity;
      for (var i = 0; i < product.variants.length; i++) {
        var vp = parseFloat(product.variants[i].price);
        if (!isNaN(vp)) {
          if (vp < pMin) pMin = vp;
          if (vp > pMax) pMax = vp;
        }
      }
      if (isFinite(pMin)) {
        card.dataset.price = livePrice.toFixed(1);
        card.dataset.priceMin = pMin.toFixed(1);
        card.dataset.priceMax = pMax.toFixed(1);
      }
    });
  }

  /* ── Look card verification ─────────────────────────────────────────── */

  function verifyLookCard(card) {
    var href = card.getAttribute('href');
    var handle = extractHandle(href);
    if (!handle) return;

    enqueue(handle, function (product) {
      if (!product) return;

      var variantId = extractVariantId(href);
      var variant = findVariantById(product, variantId);

      // Fallback to first available (.json omits `available` — only re-select when explicitly false)
      if (!variant || variant.available === false) {
        if (product.variants) {
          for (var j = 0; j < product.variants.length; j++) {
            if (product.variants[j].available !== false) {
              variant = product.variants[j];
              break;
            }
          }
        }
      }
      if (!variant) return;

      var priceEl = card.querySelector('[data-look-price]');
      if (!priceEl) return;

      var livePrice = parseFloat(variant.price);
      var liveCompare = variant.compare_at_price ? parseFloat(variant.compare_at_price) : 0;
      var displayed = parsePrices(priceEl);

      if (!needsPatch(displayed, livePrice, liveCompare)) return;

      // ── Patch price ──
      var badgeEl = card.querySelector('[data-look-badge]');
      if (liveCompare > livePrice) {
        priceEl.innerHTML = '<s class="s-product-card__compare-price">' + fmt(liveCompare) + '</s> ' + fmt(livePrice);
        if (badgeEl) {
          // Floor to match Liquid `divided_by` in look-card.liquid.
          // Invariant: displayed % must never overstate the actual discount.
          var pct = Math.floor((liveCompare - livePrice) / liveCompare * 100);
          badgeEl.textContent = pct + '% OFF';
          badgeEl.style.display = '';
        }
      } else {
        priceEl.textContent = fmt(livePrice);
        if (badgeEl) badgeEl.style.display = 'none';
      }

      // ── Patch link ──
      var base = href.replace(/[?&](variant|color)=[^&]*/g, '').replace(/\?$/, '');
      var sep = base.includes('?') ? '&' : '?';
      card.setAttribute('href', base + sep + 'variant=' + variant.id);
    });
  }

  /* ── Init ────────────────────────────────────────────────────────────── */

  function init() {
    var productCards = document.querySelectorAll('[data-product-card]');
    var lookCards = document.querySelectorAll('.s-pdp__look-card[data-look-product-id]');

    if (productCards.length === 0 && lookCards.length === 0) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          if (entry.target.hasAttribute('data-product-card')) {
            verifyProductCard(entry.target);
          } else {
            verifyLookCard(entry.target);
          }
        });
      }, { rootMargin: '200px' });

      productCards.forEach(function (c) { observer.observe(c); });
      lookCards.forEach(function (c) { observer.observe(c); });
    } else {
      // Fallback: verify all immediately
      productCards.forEach(verifyProductCard);
      lookCards.forEach(verifyLookCard);
    }
  }

  // Start after DOM ready + 1s delay to not block initial render
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(init, 1000);
    });
  } else {
    setTimeout(init, 1000);
  }
})();
